/**
 * RouteErrorBoundary — per-route crash guard (R2.2, 2026-07-16).
 *
 * WHY THIS EXISTS: before this, only 3 <ErrorBoundary> mounts existed
 * (App.js), each wrapping an entire BRANCH of the tree — the whole
 * AuthenticatedApp, the whole logged-out shell, the whole Onboarding
 * screen. A render error anywhere inside any ONE lazy route (e.g. a bad
 * prop reaching Chat, or a malformed payload breaking LearningPaths) threw
 * all the way up to that branch-level boundary, unmounting NavBar and
 * every OTHER route along with the one that actually broke — the entire
 * authenticated shell went blank over a single page's bug.
 *
 * This wraps each <Route>'s `element` individually (see App.js's route
 * tables) so a crash in one route's subtree is caught INSIDE <main>,
 * leaving NavBar/the shell mounted and every other route unaffected. The
 * catch/recover mechanics mirror components/ErrorBoundary.js exactly
 * (getDerivedStateFromError + componentDidCatch); this adds an in-place
 * "Try again" (a resetKey bump remounts just the crashed subtree, no
 * full-page reload) alongside ErrorBoundary's own hard-reload "Go Home"
 * idiom, plus the amber (never red) fallback the student-UX rule requires.
 *
 * STUDENT-UX RULE: a render crash is a SYSTEM fault, not a student
 * mistake — but it still gets the warm "encourage" amber treatment, never
 * red/alarming (this project reserves red for nothing on the student
 * surface; see tokens.css). Visually mirrors LazyRichText's own
 * chunk-load-failure fallback (rich-text.css `.rich-text__load-error`),
 * the closest existing precedent for "a piece of the page failed to
 * render, offer a calm retry" — same color tokens, same tone.
 *
 * No heading element in the fallback (deliberately a styled <p>, not
 * <h2>): this can replace ANY route's content at ANY nesting depth, so it
 * can't assume what heading level would be valid at that point in the
 * page — see landmarkInvariant.test.js / the app's heading-order sweeps
 * for why a stray heading level is treated as a real a11y bug here.
 *
 * CROSS-ROUTE KEYING (found red-first while writing App.routeErrorBoundary
 * .test.js's "other routes are unaffected" case): `<RouteErrorBoundary>` is
 * the SAME component type at every <Route>'s `element`. `<Routes>` only
 * ever renders ONE matched element into that tree position, so when the
 * matched route changes (e.g. /chat -> /paths), React's reconciler sees
 * "same type, same position" and REUSES the class instance across the
 * navigation instead of unmounting it — a crash caught on /chat left
 * `hasError: true` stuck in that reused instance, so /paths rendered the
 * STALE /chat fallback instead of LearningPaths. The default export below
 * is therefore a thin functional wrapper that keys the actual class
 * boundary on `location.pathname`: a different route means a different
 * key means React unmounts the crashed instance and mounts a genuinely
 * fresh one, discarding any stale error state. "Try again" is unaffected —
 * it doesn't touch this outer key, only the inner resetKey below.
 *
 * SAME-PATHNAME INPUT CHANGE (F2, Codex audit of PR #477): the pathname key
 * alone misses a navigation to the SAME pathname with a DIFFERENT query/hash
 * (e.g. /learn?path=p1 -> /learn?path=p2 — genuinely different route input,
 * different content). The key doesn't change, so a crash on ?path=p1 would
 * leave the stale fallback stuck over ?path=p2. We DON'T fix this by keying
 * on the full location (pathname+search+hash): that would REMOUNT the whole
 * subtree — destroying healthy route state — on ORDINARY query-only changes
 * (a ?tab= switch). Instead the wrapper passes the full location `token` as a
 * PROP and `getDerivedStateFromProps` clears `hasError` ONLY when that token
 * changes WHILE the fallback is showing. A token change on a healthy boundary
 * just records the new token (no remount, route state preserved).
 */
import React from 'react';
import { useLocation } from 'react-router';

class RouteErrorBoundaryImpl extends React.Component {
  constructor(props) {
    super(props);
    // `lastToken` seeds from the initial location token so the first
    // getDerivedStateFromProps pass is a no-op on mount.
    this.state = { hasError: false, resetKey: 0, lastToken: props.token };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  // F2: when the location token changes, the route INPUT genuinely changed.
  // If we're currently showing the fallback (same pathname, so the outer key
  // didn't remount us), clear the error so the new input renders fresh
  // instead of the stale crash UI. Only touches hasError — never forces a
  // remount, so a query-only change on a HEALTHY boundary preserves child
  // state (the "do NOT key on search" requirement). Runs every render; a
  // no-op when the token is unchanged.
  static getDerivedStateFromProps(props, state) {
    if (props.token === state.lastToken) return null;
    return state.hasError
      ? { lastToken: props.token, hasError: false }
      : { lastToken: props.token };
  }

  componentDidCatch(error, info) {
    console.error('Route error:', error, info);
  }

  // "Try again": clear the caught error AND bump resetKey. The bump is
  // what actually matters — see render()'s keyed Fragment below — it
  // forces React to fully unmount + remount the crashed subtree as a
  // BRAND NEW instance (fresh state, fresh effects) rather than resuming
  // whatever fiber just threw.
  handleReset = () => {
    this.setState((prev) => ({ hasError: false, resetKey: prev.resetKey + 1 }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            margin: '40px auto',
            maxWidth: '480px',
            padding: '32px 24px',
            textAlign: 'center',
            fontFamily: 'system-ui',
            background: 'var(--color-encourage-subtle, #FEF3C7)',
            border: '1px solid var(--color-encourage, #D97706)',
            borderRadius: 'var(--radius-md, 12px)',
            color: 'var(--color-encourage-text, #78350F)',
          }}
        >
          <p style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700 }}>
            This page hit a snag
          </p>
          <p style={{ margin: '0 0 20px' }}>
            Sorry about that — something didn&rsquo;t load right. You can
            try again, or head back to your dashboard.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={this.handleReset}
              style={{
                padding: '10px 24px',
                borderRadius: 'var(--radius-sm, 6px)',
                background: 'var(--color-encourage, #D97706)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              Try again
            </button>
            {/* Plain <a>, not a router <Link>: a route-level render crash
                means the current tree/module state is suspect, so a hard
                navigation (mirrors ErrorBoundary.js's own "Go Home"
                window.location.href reload) is the safer recovery than a
                client-side route change that keeps this render around. */}
            <a
              href="/"
              style={{
                padding: '10px 24px',
                borderRadius: 'var(--radius-sm, 6px)',
                background: 'transparent',
                color: 'var(--color-encourage-text, #78350F)',
                border: '1px solid var(--color-encourage, #D97706)',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              Go to my dashboard
            </a>
          </div>
        </div>
      );
    }
    // Keyed on resetKey so "Try again" forces a full remount of the
    // crashed subtree. A Fragment (not a <div>) so the happy path adds
    // ZERO extra DOM nodes around the route's own content.
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}

// Functional wrapper — see the CROSS-ROUTE KEYING + SAME-PATHNAME notes above.
// - `key={location.pathname}`: a different pathname (a genuinely different
//   route, e.g. /chat -> /paths) remounts a fresh boundary. Deliberately NOT
//   the full location, so a query-only change does NOT remount healthy route
//   state.
// - `token`: the full location (pathname+search+hash) passed as a PROP so the
//   boundary can clear a stale fallback when the INPUT to a same-pathname
//   route changes (e.g. /learn?path=p1 -> /learn?path=p2) — see
//   getDerivedStateFromProps above.
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  const token = `${location.pathname}${location.search}${location.hash}`;
  return (
    <RouteErrorBoundaryImpl key={location.pathname} token={token}>
      {children}
    </RouteErrorBoundaryImpl>
  );
}

export default RouteErrorBoundary;
